import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { LoginModal } from './components/LoginModal'
import { firebaseConfigured } from './lib/firebase'
import { api } from './lib/api'
import { defaultSettings, previewAstrologers } from './data/preview'
import type { Astrologer, Language, PublicSettings } from './types'
import { HomePage } from './pages/HomePage'
import { AstrologersPage } from './pages/AstrologersPage'
import { AstrologerDetailPage } from './pages/AstrologerDetailPage'
import { DashboardPage } from './pages/DashboardPage'
import { HowItWorksPage } from './pages/HowItWorksPage'
import { LegalPage } from './pages/LegalPage'
const AdminPage = lazy(() => import('./pages/AdminPage').then((module) => ({ default: module.AdminPage })))
const AstrologerPortalPage = lazy(() => import('./pages/AstrologerPortalPage').then((module) => ({ default: module.AstrologerPortalPage })))
const CallRoomPage = lazy(() => import('./pages/CallRoomPage').then((module) => ({ default: module.CallRoomPage })))

function Shell() {
  const location = useLocation()
  const [language, setLanguage] = useState<Language>(() => (localStorage.getItem('mokshm-language') as Language) || 'hi')
  const [loginOpen, setLoginOpen] = useState(false)
  const [astrologers, setAstrologers] = useState<Astrologer[]>([])
  const [settings, setSettings] = useState<PublicSettings>(defaultSettings)
  const [preview, setPreview] = useState(!firebaseConfigured || import.meta.env.VITE_ENABLE_PREVIEW_DATA === 'true')
  const special = location.pathname.startsWith('/admin') || location.pathname.startsWith('/call/')

  useEffect(() => {
    localStorage.setItem('mokshm-language', language)
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    Promise.all([api.get<{ astrologers: Astrologer[] }>('astrologers'), api.get<PublicSettings>('site-settings')])
      .then(([astroData, settingsData]) => { setAstrologers(astroData.astrologers); setSettings(settingsData); setPreview(false) })
      .catch(() => { setAstrologers(previewAstrologers); setSettings(defaultSettings); setPreview(true) })
  }, [])

  return <>
    {!special && <Header language={language} announcement={language === 'hi' ? settings.announcementHi : settings.announcementEn} onLanguageChange={setLanguage} onLogin={() => setLoginOpen(true)} />}
    <Suspense fallback={<main className="page-shell"><div className="loading-state">Loading…</div></main>}><Routes>
      <Route path="/" element={<HomePage language={language} astrologers={astrologers} settings={settings} preview={preview} />} />
      <Route path="/astrologers" element={<AstrologersPage language={language} astrologers={astrologers} preview={preview} />} />
      <Route path="/astrologers/:id" element={<AstrologerDetailPage language={language} astrologers={astrologers} onLogin={() => setLoginOpen(true)} preview={preview} />} />
      <Route path="/dashboard" element={<DashboardPage onLogin={() => setLoginOpen(true)} />} />
      <Route path="/how-it-works" element={<HowItWorksPage language={language} />} />
      <Route path="/legal/:type" element={<LegalPage />} />
      <Route path="/astrologer-portal" element={<AstrologerPortalPage onLogin={() => setLoginOpen(true)} />} />
      <Route path="/admin" element={<AdminPage onLogin={() => setLoginOpen(true)} />} />
      <Route path="/call/:bookingId" element={<CallRoomPage />} />
      <Route path="*" element={<main className="page-shell"><div className="empty-state tall"><h1>404</h1><p>यह page नहीं मिला।</p></div></main>} />
    </Routes></Suspense>
    {!special && <Footer language={language} />}
    <LoginModal language={language} open={loginOpen} onClose={() => setLoginOpen(false)} />
  </>
}

export default function App() { return <BrowserRouter><Shell /></BrowserRouter> }
